import { useId } from 'react';
import { Box } from '@mui/material';

import CustomStepperItem from 'src/components/CustomStepperItem';
import useUserStore from 'src/stores/user';
import useGoTo from 'src/hooks/useGoTo';
import {
    BEGIN_WORK_STEPS_IDS,
    FILTERED_BEGIN_WORK_STEPS_ITEM_TYPE,
    BEGIN_WORK_STEPS,
} from 'src/constants/beginWorksSteps';

import { getBeginWorkStepByOrder } from 'src/utils/beginWork';

import ApproveEmail from './ApproveEmail';

import type { CustomStepperItemProps } from 'src/components/CustomStepperItem';
import type { StepsItemType, StepsType } from 'src/types/User';

interface IGetChildrenParams {
    done: boolean;
}

interface IStepItem extends Omit<CustomStepperItemProps, 'currentStep' | 'step'> {
    getChildren?: (params: IGetChildrenParams) => React.ReactNode;
    getTitle?: (email: string) => string;
    link?: string;
    id: StepsType;
}

const STEPS_CONTENT: Partial<Record<StepsItemType, Omit<IStepItem, 'link' | 'id'>>> = {
    [BEGIN_WORK_STEPS_IDS.EMAIL_CONFIRM]: {
        getTitle: (email) => `Подтвердите почту, код отправлен ${email}`,
        titleDone: 'Почта подтверждена',
        getChildren: ({ done }) => (done ? null : <ApproveEmail />),
        isExpanded: true,
    },
    [BEGIN_WORK_STEPS_IDS.DESCRIPTION]: {
        title: 'Расскажите о своём бизнесе',
    },
    [BEGIN_WORK_STEPS_IDS.FORMAT]: {
        title: 'Запуск сервиса',
    },
    [BEGIN_WORK_STEPS_IDS.CHOOSE_STREAM]: {
        title: 'Запуск сервиса',
    },
    [BEGIN_WORK_STEPS_IDS.CREATE_PROJECT]: {
        title: 'Создание проекта',
    },
    [BEGIN_WORK_STEPS_IDS.ADD_KEYWORDS]: {
        title: 'Добавление ключевых слов',
    },
    [BEGIN_WORK_STEPS_IDS.CHOOSE_COLLECTIONS]: {
        title: 'Выбор подборок',
    },
    [BEGIN_WORK_STEPS_IDS.ADD_CHATS]: {
        title: 'Добавление чатов',
    },
    [BEGIN_WORK_STEPS_IDS.CHOOSE_PLAN]: {
        title: 'Выбор тарифа',
    },
};

const STEPS: IStepItem[] = FILTERED_BEGIN_WORK_STEPS_ITEM_TYPE.map((step) => ({
    ...step,
    ...(STEPS_CONTENT[step.id] || {}),
}));

export default function Steps() {
    const { onboardingStep, onboardingStepName, current_subscription, email } = useUserStore(
        ({ onboardingStep, onboardingStepName, current_subscription, email }) => ({
            onboardingStep,
            onboardingStepName,
            current_subscription,
            email,
        })
    );

    const currentStep = onboardingStep || 1;
    // Используем строковое значение шага из store, если есть, иначе получаем из порядка
    const currentStepName = onboardingStepName || getBeginWorkStepByOrder(currentStep)?.id;

    const randomId = useId();
    const goTo = useGoTo();

    // Всегда показываем только 3 базовых шага: email, описание бизнеса, запуск сервиса (/leadstream)
    const baseStepIds: StepsType[] = [
        BEGIN_WORK_STEPS_IDS.EMAIL_CONFIRM,
        BEGIN_WORK_STEPS_IDS.DESCRIPTION,
        BEGIN_WORK_STEPS_IDS.CHOOSE_STREAM,
    ];

    const visibleSteps = STEPS.filter((step) => baseStepIds.includes(step.id));

    // Определяем порядок шагов для отображения
    const getStepDisplayOrder = (stepId: StepsType) => {
        const baseIndex = baseStepIds.indexOf(stepId);
        return baseIndex >= 0 ? baseIndex + 1 : 999;
    };

    // Сортируем видимые шаги по порядку
    const sortedVisibleSteps = [...visibleSteps].sort((a, b) => {
        const orderA = getStepDisplayOrder(a.id);
        const orderB = getStepDisplayOrder(b.id);
        return orderA - orderB;
    });

    // Определяем текущий шаг для отображения (порядковый номер среди видимых шагов)
    const getCurrentStepNumber = () => {
        if (!currentStepName) return currentStep;
        const currentIndex = sortedVisibleSteps.findIndex((step) => step.id === currentStepName);
        // Если текущий шаг не в списке видимых, определяем по базовым шагам
        if (currentIndex < 0) {
            const baseIndex = baseStepIds.findIndex((id) => id === currentStepName);
            if (baseIndex >= 0) {
                return baseIndex + 1;
            }
            // Если шаг уже пройден (например, create_project и далее), показываем последний шаг как текущий
            return 3; // Запуск сервиса - последний видимый шаг
        }
        return currentIndex >= 0 ? currentIndex + 1 : currentStep;
    };

    const displayCurrentStep = getCurrentStepNumber();

    return (
        <Box
            display="flex"
            flexDirection="column"
            gap="16px"
        >
            {sortedVisibleSteps.map(({ getChildren, isExpanded, id, link, getTitle, title, ...props }, index) => {
                const itemStep = index + 1;
                const done = displayCurrentStep > itemStep;

                return (
                    <CustomStepperItem
                        key={`${randomId}_${title || id}`}
                        step={itemStep}
                        currentStep={displayCurrentStep}
                        done={done}
                        isExpanded={done ? false : isExpanded}
                        onClick={link ? () => goTo(link) : undefined}
                        title={getTitle?.(email) || title}
                        {...props}
                    >
                        {getChildren?.({ done })}
                    </CustomStepperItem>
                );
            })}
        </Box>
    );
}
